import { type IPromise } from 'angular';
import { A9Promise } from 'components/system/promise';

/**
 * Converts a IPromise to a Promise
 *
 * This is useful if you use angular's $http pr $q service, and it returns an IPromise.
 *
 * The IPromise is not directly compatible with the web Promise type.
 * This wrapper handles the success and error states and converts them to a Promise
 *
 * To use this, you just wrap the calls that return the iPromise
 *
 * @example ```typescript
 * return IPromiseToPromise(
 *          this.$http.post(`middle://${this.path}`, { name: '' }).then(response => {
 *              const details = <ITournamentTeamsSelection>response.data;
 *              this._updateState(details.id, 0);
 *          })
 *      );
 * ```
 *
 * Or multiple .then's
 *
 * @example```typescript
 * IPromiseToPromise(
 *          this._$http
 *              .post(SELECTION_URL, <SeContainerAdd>{
 *                  name: '',
 *                  inverted: true
 *              })
 *              .then(response => {
 *                  const details = <RsSeLockerInfo>response.data;
 *                  this._updateState(details.id, details.objects.length);
 *              })
 *      );
 * ```
 *
 * @param oldPromise IPromise that should be converted
 * @returns Promise
 */
export async function IPromiseToPromise<Response>(oldPromise: IPromise<Response>): Promise<Response> {
    const promise = new A9Promise<Response>();
    try {
        const res = await oldPromise;
        promise.resolve(res);
    } catch (error) {
        promise.reject(error);
    }

    return promise.promise;
}
